<template>
  <div class="permission-tree">
    <div v-if="!ready" class="text-center p-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="filteredCategories.length === 0" class="text-center p-4 text-muted">
      No permissions available for this user type.
    </div>

    <div v-else>
      <div
        v-for="cat in filteredCategories"
        :key="cat.id"
        class="perm-category my-3"
      >
        <h5>{{ cat.name }}</h5>

        <div class="table-responsive">
          <table class="table table-sm table-bordered permission-matrix mb-0">
            <thead>
              <tr>
                <th class="perm-name-col">Permission</th>
                <th
                  v-for="col in columns"
                  :key="col.key"
                  class="text-center perm-col"
                >
                  <span class="perm-col-header">
                    {{ col.label }}
                    <label
                      class="checkboxs"
                      :class="{ indeterminate: isColumnPartiallySelected(cat, col.key) }"
                    >
                      <input
                        type="checkbox"
                        :checked="isColumnFullySelected(cat, col.key)"
                        :indeterminate.prop="isColumnPartiallySelected(cat, col.key)"
                        @change="toggleColumn(cat, col.key, $event.target.checked)"
                      />
                      <span class="checkmarks"></span>
                    </label>
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in cat.items" :key="item.id">
                <td>{{ item.name }}</td>
                <td
                  v-for="col in columns"
                  :key="`${item.id}-${col.key}`"
                  class="text-center"
                >
                  <label
                    v-if="isFlagAllowed(item, col.flag)"
                    class="checkboxs"
                  >
                    <input
                      type="checkbox"
                      :checked="hasFlag(item.id, col.flag)"
                      @change="toggleFlag(item, col.flag, $event.target.checked)"
                    />
                    <span class="checkmarks"></span>
                  </label>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const FLAG_READ = "R";
const FLAG_ADD = "W";
const FLAG_EDIT = "E";

// Permissions that only support Read (no Edit / ADD / ALL)
const READ_ONLY_CODES = new Set(["inventory.variant"]);

function levelToFlags(level) {
  const value = level || "N";
  if (value === "N") return { R: false, W: false, E: false };
  return {
    R: value.includes(FLAG_READ),
    W: value.includes(FLAG_ADD),
    E: value.includes(FLAG_EDIT),
  };
}

function flagsToLevel(flags) {
  let level = "";
  if (flags.R) level += FLAG_READ;
  if (flags.W) level += FLAG_ADD;
  if (flags.E) level += FLAG_EDIT;
  return level || "N";
}

export default {
  props: {
    categories: {
      type: Array,
      default: () => []
    },
    selected: {
      type: Object,
      required: true
    },
    userTypePermissions: {
      type: Array,
      default: () => []
    },
    userType: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      localSelected: {},
      ready: false,
      columns: [
        { key: "read", label: "Read", flag: FLAG_READ },
        { key: "edit", label: "Edit", flag: FLAG_EDIT },
        { key: "add", label: "ADD", flag: FLAG_ADD },
        { key: "all", label: "ALL", flag: "ALL" },
      ],
    };
  },

  watch: {
    selected: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.localSelected = { ...newVal };
        this.ready = true;
      }
    },

    userTypePermissions: {
      immediate: true,
      handler(newVal) {
        const allowedIds = (newVal || []).map(p => p.id);
        const filtered = {};

        Object.keys(this.localSelected).forEach(key => {
          const permId = Number(key);
          if (allowedIds.includes(permId)) {
            filtered[permId] = this.localSelected[key];
          }
        });

        this.localSelected = filtered;
        this.ready = true;
      }
    }
  },

  computed: {
    filteredCategories() {
      if (!Array.isArray(this.userTypePermissions) || this.userTypePermissions.length === 0) {
        return [];
      }

      const allowedIds = this.userTypePermissions.map(p => p.id);

      return this.categories
        .map(category => {
          if (!category || !Array.isArray(category.items)) {
            return null;
          }

          const filteredItems = category.items.filter(item =>
            item && item.id && allowedIds.includes(item.id)
          );

          if (filteredItems.length === 0) {
            return null;
          }

          return {
            ...category,
            items: filteredItems.sort((a, b) => a.order - b.order)
          };
        })
        .filter(category => category !== null);
    }
  },

  methods: {
    isReadOnly(item) {
      return READ_ONLY_CODES.has(item?.code);
    },

    isFlagAllowed(item, flag) {
      if (!this.isReadOnly(item)) return true;
      return flag === FLAG_READ;
    },

    itemsForColumn(category, columnKey) {
      const col = this.columns.find(c => c.key === columnKey);
      if (!col) return [];
      return category.items.filter(item => this.isFlagAllowed(item, col.flag));
    },

    hasFlag(permissionId, flag) {
      const flags = levelToFlags(this.localSelected[permissionId]);
      if (flag === "ALL") {
        return flags.R && flags.W && flags.E;
      }
      return !!flags[flag];
    },

    setLevel(permissionId, level) {
      const updated = { ...this.localSelected };

      if (!level || level === "N") {
        delete updated[permissionId];
      } else {
        updated[permissionId] = level;
      }

      this.localSelected = updated;
      this.$emit("update", {
        id: permissionId,
        level: level || "N",
      });
    },

    toggleFlag(item, flag, checked) {
      if (!this.isFlagAllowed(item, flag)) return;

      if (this.isReadOnly(item)) {
        this.setLevel(item.id, checked ? "R" : "N");
        return;
      }

      if (flag === "ALL") {
        this.setLevel(item.id, checked ? "RWE" : "N");
        return;
      }

      const flags = levelToFlags(this.localSelected[item.id]);
      flags[flag] = checked;
      this.setLevel(item.id, flagsToLevel(flags));
    },

    isColumnFullySelected(category, columnKey) {
      const items = this.itemsForColumn(category, columnKey);
      if (!items.length) return false;
      const col = this.columns.find(c => c.key === columnKey);
      return items.every(item => this.hasFlag(item.id, col.flag));
    },

    isColumnPartiallySelected(category, columnKey) {
      const items = this.itemsForColumn(category, columnKey);
      if (!items.length) return false;
      const col = this.columns.find(c => c.key === columnKey);
      const selectedCount = items.filter(item =>
        this.hasFlag(item.id, col.flag)
      ).length;
      return selectedCount > 0 && selectedCount < items.length;
    },

    toggleColumn(category, columnKey, checked) {
      const col = this.columns.find(c => c.key === columnKey);
      if (!col) return;

      this.itemsForColumn(category, columnKey).forEach(item => {
        this.toggleFlag(item, col.flag, checked);
      });
    },
  }
};
</script>

<style scoped>
.permission-matrix th,
.permission-matrix td {
  vertical-align: middle;
}

.perm-name-col {
  min-width: 180px;
}

.perm-col {
  width: 100px;
  min-width: 100px;
}

.perm-col-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

/* Compact theme checkboxes for the matrix */
.permission-matrix :deep(.checkboxs) {
  display: inline-block;
  padding-left: 16px;
  margin-bottom: 0;
  min-height: 16px;
  line-height: 16px;
  vertical-align: middle;
}

.permission-matrix :deep(.checkboxs .checkmarks) {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.permission-matrix :deep(.checkboxs .checkmarks:after) {
  left: 5px;
  top: 2px;
  width: 5px;
  height: 8px;
}

.permission-matrix :deep(.checkboxs.indeterminate .checkmarks) {
  background-color: #FE9F43;
  border-color: #FE9F43;
}

.permission-matrix :deep(.checkboxs.indeterminate .checkmarks:after) {
  display: block;
  left: 3px;
  top: 6px;
  width: 8px;
  height: 0;
  border-width: 0 0 2px 0;
  transform: none;
}
</style>
